/**
 * Web 设备指纹合成(对齐 rn-app `src/native/DeviceFingerprint.ts` 的语义)。
 *
 * 后端只对认证路径(register/**、sms/send、login/**)校验 header `X-Device-Fingerprint`
 * 是否为 64 位小写 hex(调研§4)。Web 无硬件指纹,合成一个稳定值即可:
 *   fields = { deviceId(持久化 UUID)+ 浏览器信号 } → sha256Hex → 64-hex。
 *
 * deviceId 持久化到 localStorage(web 等价 rn 的 MMKV),保证同浏览器稳定。
 * 这是 ADR-0027 / tech-plan §2.3 指定的「设备指纹合成」路径;**不走** ops-console 的
 * X-Admin-Client-Token 绕过(那是 admin 专用)。
 */
import { sha256Hex } from './sha256';

const DEVICE_ID_KEY = 'workability-web.fingerprint.deviceId';

function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = generateUuid();
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    // localStorage 不可用(隐私模式等):退而求其次,用一次性 UUID(本会话稳定)。
    return generateUuid();
  }
}

function generateUuid(): string {
  if (typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  // 回退:RFC4122 v4 形状(非加密强度,仅作 deviceId 种子)。
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function collectSignals(): Record<string, string> {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const scr = typeof screen !== 'undefined' ? screen : ({ width: 0, height: 0 } as Screen);
  const min = Math.min(scr.width, scr.height);
  const max = Math.max(scr.width, scr.height);
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    timezone = '';
  }
  return {
    deviceId: getOrCreateDeviceId(),
    userAgent: nav.userAgent ?? '',
    platform: nav.platform ?? '',
    language: nav.language ?? '',
    screenSize: `${min}x${max}`,
    timezone,
  };
}

let cached: string | null = null;

/** 返回 64 位小写 hex 指纹(进程内缓存一次)。 */
export async function getDeviceFingerprint(): Promise<string> {
  if (cached) return cached;
  const fields = collectSignals();
  cached = await sha256Hex(JSON.stringify(fields));
  return cached;
}

/** 仅供测试:重置进程内缓存。 */
export function __resetFingerprintCache(): void {
  cached = null;
}
